import React, { FC } from 'react';

import { Stack, Box, Typography, GridContainer, GridItem, useMediaSwitch, useMobile, useDesktop } from 'Modules/ui3';
import { fileUrl } from 'Modules/ui2';
import { useAction } from 'Modules/core/redux';
import { messagingActions } from 'Modules/core/messaging';

import { ContentBox } from '../../components/ContentBox';
import { AccentButton } from '../../components/AccentButton';
import { data } from './data';
import { InfoBlock } from '../../components/InfoBlock';
import { NumberedSteps } from '../../components/NumberedSteps';
import IssueBg from './assets/issue_bg.svg';
import IssueImage from './assets/issue.png';
import BuyBg from './assets/buy_bg.svg';
import BuyImage from './assets/buy.png';
import Showcase from './assets/Showcase.png';
import SellBg from './assets/sell_bg.svg';
import SellImage from './assets/Case.png';
import { News } from '../../components/News';
import { getIdFromAnchor } from '../../utils';
import { landingAnchorRoutes } from '../../routes/landingAnchorRoutes';
import GrinningFace from './assets/GrinningFace.png';
import { PageContainer } from '../../components/PageContainer';
import { SectionTitleH1, TitleH1, TitleH2, TitleH3 } from '../../components/Titles';
import { Partners } from '../../components/Partners';
import { Role } from '../../components/FeedbackForm/types';
import { MonitoringBlock } from '../../components/MonitoringBlock';
import { Paragraph } from '../../components/Paragraph';
import { MainInfo } from '../../components/MainInfo';


export const Landing: FC = () => {
    const isMobile = useMobile();
    const isDesktop = useDesktop();

    const showFeedback = useAction(() => messagingActions.showModal({
        type: 'FeedbackModal',
        closeable: false,
    }));

    const showFeedbackIssuer = useAction(() => messagingActions.showModal({
        type: 'FeedbackModal',
        closeable: false,
        props: {
            formValues: {
                role: Role.ISSUER,
            },
        },
    }));

    const showPartnerContacts = useAction(() => messagingActions.showModal({
        type: 'ContactsModal',
    }));

    return (
        <PageContainer>
            <MainInfo />
            <MonitoringBlock />

            <SectionTitleH1>
                Преимущества ЦФА
            </SectionTitleH1>
            <Paragraph color='grayscale.shade90' maxWidth='915px'>
                Цифровые финансовые активы — новый финансовый инструмент на российском рынке.
                Он&nbsp;позволяет привлечь инвестиции быстрее и дешевле, а также легко настраивается
                под&nbsp;конкретные бизнес-задачи за счет использования технологии блокчейн
            </Paragraph>
            <GridContainer spacing={2} columns={4} mt={isDesktop ? 10 : 4}>
                {data.advantages.map((item, idx) => (
                    <GridItem key={`item_${idx}`} desktop={1} tablet={2} mobile={4}>
                        <InfoBlock data={item} height='100%' />
                    </GridItem>
                ))}
            </GridContainer>
            <SectionTitleH1>
                Цифровые продукты
            </SectionTitleH1>
            <Stack spacing={2} direction={isMobile ? 'column' : 'row'} mt={isDesktop ? 10 : 6}>
                {data.ideas.map((item, idx) => (
                    <InfoBlock key={`item_${idx}`} data={item} flex={1} />
                ))}
            </Stack>
            <SectionTitleH1>
                Важно знать
            </SectionTitleH1>
            <GridContainer columns={4} spacing={2} mt={isDesktop ? 10 : 4}>
                {data.toKnow.map((item, idx) => (
                    <GridItem key={`item_${idx}`} desktop={1} tablet={2} mobile={4}>
                        <InfoBlock data={{ title: item }} height='100%' />
                    </GridItem>
                ))}
            </GridContainer>
            <ContentBox
                id={getIdFromAnchor(landingAnchorRoutes.issuers.route)}
                mt={isDesktop ? 16 : 12}
                px={useMediaSwitch(8, 6, 4)}
                pt={useMediaSwitch(8, 6, 8)}
                pb={useMediaSwitch(8, 6, 6)}
                backgroundImage={`url("${fileUrl(IssueBg)}")`}
            >
                <Stack direction={isMobile ? 'column' : 'row'} spacing={useMediaSwitch(37.75, 15, 10)}>
                    <Box flex={1}>
                        <TitleH3>
                            Эмитентам
                        </TitleH3>
                        <TitleH1 mt={useMediaSwitch(14, 8, 5)}>
                            Выпускайте ЦФА
                        </TitleH1>
                        <Paragraph>
                            Привлекайте средства быстрее, дешевле, без посредников
                        </Paragraph>
                        <AccentButton
                            onClick={showFeedbackIssuer}
                            sx={{
                                mt: isDesktop ? 10 : 6,
                            }}
                        >
                            Заявка на привлечение
                        </AccentButton>
                    </Box>
                    <Stack
                        flex={useMediaSwitch(0.8, 1.15, 1)}
                        justifyContent='center'
                        pr={isDesktop && 23}
                        pt={useMediaSwitch(10.25, 9.25, 0)}
                        pb={!isMobile && 9.25}
                    >
                        <img src={fileUrl(IssueImage)} width='100%' height='auto' alt='Выпускайте ЦФА' />
                    </Stack>
                </Stack>
            </ContentBox>
            <ContentBox mt={2} px={useMediaSwitch(8, 6, 4)} py={isDesktop ? 12 : 6}>
                <TitleH2>
                    Эмитентом ЦФА может стать любая компания
                </TitleH2>
                <Stack spacing={2} direction={isMobile ? 'column' : 'row'} mt={isDesktop ? 10 : 5}>
                    {data.issuerCompanies.map((item, idx) => (
                        <InfoBlock
                            key={`item_${idx}`}
                            data={{ title: item }}
                            flex={1}
                            p={isDesktop ? 6 : 3}
                            borderRadius={5}
                            borderColor='grayscale.shade40'
                        />
                    ))}
                </Stack>
                <TitleH2 mt={isDesktop ? 12 : 8}>
                    Выпустить ЦФА на платформе просто
                </TitleH2>
                <NumberedSteps steps={data.releaseDfaSteps} mt={isDesktop ? 10 : 5} />
                <AccentButton
                    onClick={showFeedbackIssuer}
                    sx={{
                        mt: isDesktop ? 12 : 8,
                    }}
                >
                    Заявка на привлечение
                </AccentButton>
            </ContentBox>
            <ContentBox
                id={getIdFromAnchor(landingAnchorRoutes.investors.route)}
                mt={isDesktop ? 16 : 12}
                px={useMediaSwitch(8, 6, 4)}
                pt={useMediaSwitch(8, 6, 8)}
                pb={useMediaSwitch(8, 6, 6)}
                backgroundImage={`url("${fileUrl(BuyBg)}")`}
            >
                <Stack direction={isMobile ? 'column' : 'row'} spacing={useMediaSwitch(37.75, 15, 10)}>
                    <Box flex={1}>
                        <TitleH3>
                            Инвесторам
                        </TitleH3>
                        <TitleH1 mt={useMediaSwitch(14, 8, 5)}>
                            Покупайте ЦФА
                        </TitleH1>
                        <Paragraph>
                            Диверсифицируйте портфель за счет доходных цифровых&nbsp;активов
                        </Paragraph>
                        <AccentButton
                            onClick={showFeedback}
                            sx={{
                                mt: isDesktop ? 10 : 6,
                            }}
                        >
                            Заявка на инвестирование
                        </AccentButton>
                    </Box>
                    <Stack
                        flex={useMediaSwitch(0.8, 1.15, 1)}
                        justifyContent='center'
                        pr={isDesktop && 23}
                        pt={useMediaSwitch(10.25, 9.25, 0)}
                        pb={!isMobile && 9.25}
                    >
                        <img src={fileUrl(BuyImage)} width='100%' height='auto' alt='Покупайте ЦФА' />
                    </Stack>
                </Stack>
            </ContentBox>
            <ContentBox mt={2} py={isDesktop ? 12 : 6} px={useMediaSwitch(8, 6, 4)}>
                <TitleH2>
                    Заработать на ЦФА получится у каждого
                </TitleH2>
                <Stack direction={isMobile ? 'column' : 'row'} mt={isDesktop ? 10 : 5}>
                    <Box flex={1} pr={!isMobile && 2}>
                        <InfoBlock
                            p={isDesktop ? 5.5 : 3}
                            mb={isMobile && 2}
                            borderRadius={5}
                            borderColor='grayscale.shade40'
                            data={data.clients.unqualified}
                            height='100%'
                        />
                    </Box>
                    <GridContainer columns={2} flex={1} spacing={2} height={isDesktop ? 'auto' : 'fit-content'}>
                        {data.clients.arr.map((item, idx) => (
                            <GridItem key={`item_${idx}`} desktop={1} tablet={2} mobile={2} height={isDesktop ? 'auto' : 'fit-content'} minHeight={isDesktop && '214px'}>
                                <InfoBlock p={isDesktop ? 5.5 : 3} borderRadius={5} height='100%' data={item} borderColor='grayscale.shade40' />
                            </GridItem>
                        ))}
                    </GridContainer>
                </Stack>
                <TitleH2 mt={useMediaSwitch(12, 8, 5)}>
                    Купить подходящий актив можно на платформе ЦФА ХАБ
                </TitleH2>
                <Box mt={isDesktop ? 10 : 5}>
                    <img src={fileUrl(Showcase)} width='100%' alt='Витрина' />
                </Box>
                <AccentButton
                    onClick={showFeedback}
                    sx={{
                        mt: isDesktop ? 12 : 8,
                    }}
                >
                    Заявка на инвестирование
                </AccentButton>
            </ContentBox>
            <ContentBox
                id={getIdFromAnchor(landingAnchorRoutes.partners.route)}
                mt={isDesktop ? 16 : 12}
                py={useMediaSwitch(2, 6, 8)}
                px={useMediaSwitch(8, 6, 4)}
                backgroundImage={isDesktop && `url("${fileUrl(SellBg)}")`}
            >
                <Stack direction={isMobile ? 'column' : 'row'} spacing={useMediaSwitch(31.5, 9, 8)}>
                    <Box flex={1} pt={isDesktop && 6} pb={useMediaSwitch(20, 2, 0)}>
                        <TitleH3>
                            Партнерам
                        </TitleH3>
                        <TitleH1 mt={useMediaSwitch(14, 8, 5)}>
                            Откройте вашим клиентам доступ к ЦФА
                        </TitleH1>
                        <Paragraph>
                            Подключайтесь к ЦФА ХАБ через API и добавляйте новые
                            инструменты без изменения клиентского пути
                        </Paragraph>
                        <AccentButton
                            onClick={showPartnerContacts}
                            sx={{
                                mt: isDesktop ? 10 : 6,
                            }}
                        >
                            Стать партнером
                        </AccentButton>
                    </Box>
                    <Stack
                        flex={useMediaSwitch(0.8, 1.15, 1)}
                        justifyContent='center'
                        pr={useMediaSwitch(27.5, 6, 0)}
                    >
                        <img src={fileUrl(SellImage)} width='100%' height='auto' alt='Продавайте ЦФА' />
                    </Stack>
                </Stack>
            </ContentBox>
            <ContentBox mt={2} py={isDesktop ? 12 : 6} px={useMediaSwitch(8, 6, 4)}>
                <TitleH2>
                    Помогаем зарабатывать вместе с нами!
                </TitleH2>
                <Stack direction='row' spacing={2} mt={isDesktop ? 5 : 3} alignItems={isDesktop ? 'center' : 'flex-start'}>
                    <img src={fileUrl(GrinningFace)} alt='smile' width={isDesktop ? 40 : 24} height='auto' />
                    <Typography variant={isDesktop ? 'regularHeader2' : 'regularBody2'} lineHeight={isDesktop ? '1.5' : '1.4'} color='grayscale.shade95'>
                        Комиссии за сделки с ЦФА ваших клиентов делятся между платформой и партнером
                    </Typography>
                </Stack>
                <Stack mt={isDesktop ? 10 : 5} direction={isMobile ? 'column' : 'row'} spacing={2}>
                    {data.helps.map((item, idx) => (
                        <InfoBlock
                            key={`item_${idx}`}
                            flex={1}
                            p={isDesktop ? 6 : 3}
                            data={item}
                            borderRadius={isDesktop ? 8 : 5}
                            borderColor='grayscale.shade40'
                        />
                    ))}
                </Stack>
                <TitleH2 mt={useMediaSwitch(12, 8, 5)}>
                    Дополнительные услуги для клиентов и партнеров
                </TitleH2>
                <GridContainer columns={3} mt={isDesktop ? 8 : 3} spacing={2} alignItems='stretch'>
                    {data.additional.map((item, idx) => (
                        <GridItem key={`item_${idx}`} desktop={1} tablet={1} mobile={3}>
                            <InfoBlock
                                p={isDesktop ? 6 : 3}
                                borderRadius={5}
                                borderColor='grayscale.shade40'
                                height='100%'
                                minHeight={isDesktop ? '110px' : isMobile ? '50px' : '74px'}
                                data={{ title: item }}
                            />
                        </GridItem>
                    ))}
                </GridContainer>
                <AccentButton
                    onClick={showPartnerContacts}
                    sx={{
                        mt: isDesktop ? 12 : 8,
                    }}
                >
                    Стать партнером
                </AccentButton>
            </ContentBox>
            <Partners />
            <News id={getIdFromAnchor(landingAnchorRoutes.news.route)} rowCount={2} />
        </PageContainer>
    );
};
